import React, { useEffect, useState, useContext } from "react";
import { formatPhone } from "../../utils/formatPhone";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const EditCounselorForm = ({ counselorId, onDone }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    rsa_email: "",
    phone_number: "",
    customer_id: "",
    status_id: "",
    tenant_name: "",
    branch_id: "", // Changed from branch text to branch_id
  });
  const [customers, setCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { user, tenant_id, is_primary } = useContext(AuthContext);

  useEffect(() => {
    if (!counselorId || !tenant_id) return;

    setLoading(true);

    // First load counselor details
    secureAxios
      .get(`/api/counselors/${counselorId}`, {
        params: { tenant_id, is_primary },
      })
      .then((res) => {
        const data = res.data;

        setFormData({
          name: data.name || "",
          email: data.email || "",
          rsa_email: data.rsa_email || "",
          phone_number: data.phone_number || "",
          customer_id: data.customer_id || "",
          status_id: data.status_id || "",
          tenant_name: data.tenant_name || "",
          branch_id: data.branch_id || "", // Load branch_id
        });

        // Load branches for the selected customer
        let branchUrl = `/api/branches/dropdown?tenant_id=${tenant_id}`;
        if (data.customer_id) {
          branchUrl += `&customer_id=${data.customer_id}`;
        }
        return Promise.all([
          secureAxios.get(`/api/customers`, {
            params: {
              tenant_id,
              is_primary,
              include_customer_id: data.customer_id,
            },
          }),
          secureAxios.get(branchUrl),
        ]);
      })
      .then(([customersRes, branchesRes]) => {
        setCustomers(customersRes.data);
        setBranches(branchesRes.data);
      })
      .catch((err) => {
        console.error("Failed to load counselor:", err.message);
        setMessage("Error loading counselor data: " + err.message);
      })
      .finally(() => {
        setLoading(false);
      });

    // Load statuses
    secureAxios
      .get(`/api/status`, {
        params: { tenant_id, type: "Counselor" },
      })
      .then((res) => {
        setStatuses(res.data);
      })
      .catch((err) => {
        console.error("Error loading statuses:", err.message);
        setMessage("Error loading statuses: " + err.message);
      });
  }, [counselorId, tenant_id, is_primary]);

  // Reload branches when customer changes
  useEffect(() => {
    if (!tenant_id) return;

    let url = `/api/branches/dropdown?tenant_id=${tenant_id}`;
    if (formData.customer_id) {
      url += `&customer_id=${formData.customer_id}`;
    }

    secureAxios
      .get(url)
      .then((res) => {
        setBranches(res.data);
        // Clear branch selection if current branch not in filtered list
        if (
          formData.branch_id &&
          !res.data.some((b) => b.branch_id === formData.branch_id)
        ) {
          setFormData((prev) => ({ ...prev, branch_id: "" }));
        }
      })
      .catch((err) => {
        console.error("Error loading branches:", err.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customer_id, tenant_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === "phone_number" ? value.replace(/\D/g, "") : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("Saving...");
    setLoading(true);

    try {
      await secureAxios.put(`/api/counselors/${counselorId}`, {
        ...formData,
        branch_id: formData.branch_id || null, // Send branch_id
        tenant_id,
        updated_by: user?.user_id,
        is_primary,
      });

      setMessage("Counselor updated successfully!");

      if (onDone) {
        setTimeout(() => onDone(), 1500);
      }
    } catch (err) {
      console.error("Error updating counselor:", err.message);
      const backendMessage =
        err?.response?.data?.error || "Unknown server error.";
      setMessage("Error saving changes: " + backendMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.name) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading counselor data...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="row g-3">
      {message && (
        <div className="col-12">
          <div
            className={`alert ${
              message.includes("successfully")
                ? "alert-success"
                : message.includes("Error") || message.includes("error")
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {message}
          </div>
        </div>
      )}

      <div className="col-md-6">
        <label className="form-label">Name *</label>
        <input
          className="form-control"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          disabled={loading}
          autoComplete="name"
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Email *</label>
        <input
          className="form-control"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          disabled={loading}
          autoComplete="email"
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Additional Email (RSA)
          <i
            className="bi bi-info-circle ms-1 text-muted"
            title="RSA email will auto-populate in Email Document Bundle"
            style={{ cursor: "help" }}
          ></i>
        </label>
        <input
          className="form-control"
          name="rsa_email"
          type="email"
          value={formData.rsa_email}
          onChange={handleChange}
          disabled={loading}
          autoComplete="email"
          placeholder="rsa@example.com"
        />
        <div className="form-text">
          Auto-populates "Additional Recipients" when emailing documents
        </div>
      </div>

      <div className="col-md-6">
        <label className="form-label">Phone Number *</label>
        <input
          className="form-control"
          name="phone_number"
          value={formatPhone(formData.phone_number)}
          onChange={handleChange}
          required
          disabled={loading}
          autoComplete="tel"
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Customer *</label>
        <select
          className="form-select"
          name="customer_id"
          value={formData.customer_id || ""}
          onChange={handleChange}
          required
          disabled={loading}
        >
          <option value="">Select Customer</option>
          {customers.map((c) => (
            <option key={c.customer_id} value={c.customer_id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="col-md-6">
        <label className="form-label">Status *</label>
        <select
          className="form-select"
          name="status_id"
          value={formData.status_id}
          onChange={handleChange}
          required
          disabled={loading}
        >
          <option value="">Select Status</option>
          {statuses.map((s) => (
            <option key={s.status_id} value={s.status_id}>
              {s.status}
            </option>
          ))}
        </select>
      </div>

      {/* Branch/Office Dropdown */}
      <div className="col-md-6">
        <label className="form-label">
          Branch/Office
          <i
            className="bi bi-info-circle ms-1 text-muted"
            title="Used on invoice billing address"
            style={{ cursor: "help" }}
          ></i>
        </label>
        <select
          className="form-select"
          name="branch_id"
          value={formData.branch_id}
          onChange={handleChange}
          disabled={loading}
        >
          <option value="">-- Select Branch/Office --</option>
          {branches.map((b) => (
            <option key={b.branch_id} value={b.branch_id}>
              {b.branch_name}
            </option>
          ))}
        </select>
        <div className="form-text">
          Branch or office name displayed on invoice "Bill To" section
        </div>
      </div>

      {is_primary && (
        <div className="col-md-6">
          <label className="form-label">Tenant</label>
          <input
            className="form-control"
            value={formData.tenant_name || "-"}
            disabled
            readOnly
          />
        </div>
      )}

      <div className="col-12">
        <button type="submit" className="btn btn-success" disabled={loading}>
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </button>

        <button
          type="button"
          className="btn btn-secondary ms-2"
          onClick={onDone}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default EditCounselorForm;